// Die Aenderungsschluessel der Panel-Listen.
//
// Panel-Listen sind Template-String-`innerHTML` und laufen im
// `requestAnimationFrame`-Takt. Sie bauen nur neu, wenn ihr Schluessel kippt —
// und **darin** steckte ein Fehler, der wie „der Server kommt nicht mit" aussah:
// enthaelt der Schluessel eine Zahl, die sich laufend aendert, wird die Liste
// samt Knoepfen mehrmals pro Sekunde ersetzt, und ein Klick, dessen Druck und
// Loslassen ein Rerender trennt, feuert nie. Der Knopf wird dem Spieler unter
// dem Finger weggezogen.
//
// Die Regel gilt in **beide** Richtungen, und das ist der Kern:
//
// * Wer ein Feld in die Darstellung aufnimmt, das der Schluessel nicht erfasst,
//   baut einen Fehler ein, der aussieht wie „aktualisiert manchmal nicht".
// * Wer eine Groesse in den Schluessel aufnimmt, die **feiner** ist als das, was
//   die Liste zeigt, baut einen Fehler ein, der aussieht wie „mein Klick kommt
//   nicht an".
//
// Ein Schluessel bildet deshalb ab, **was die Liste zeigt**, nicht **woraus sie
// es rechnet**: Schwellen statt Zahlen. Vokabular und Vorbild sind
// `recipeListKey` in `recipes.ts` — dort ist derselbe Fehler zuerst aufgefallen,
// weil man am Konverter denselben Knopf zwanzigmal druecke.
//
// Was eine Liste als **laufende Zahl** zeigt (Countdown, Fortschritt), gehoert
// nicht in den Schluessel, sondern wird an Ort und Stelle geschrieben — siehe
// `refreshBoosterLive` und `refreshQuestLive` in `panels.ts`. Das ist derselbe
// Gedanke wie bei `setMoveBusy`: was jeden Neubau ueberleben soll, haengt nicht
// am `innerHTML`.

import { balancing, boosters, builds, fleetOrder, quests, sideQuests } from '../data/balancing';

/**
 * Ein Bit je Kostenposten: gedeckt oder nicht.
 *
 * Daraus folgt auch „bezahlbar", denn bezahlbar heisst „jeder Posten gedeckt" —
 * ein eigenes Bit dafuer waere dieselbe Aussage ein zweites Mal.
 *
 * Sie steht **hier** und wird von `recipes.ts` mitbenutzt: dort ist der Fehler
 * zuerst aufgefallen, und die Zeile stand danach zeichengleich zweimal da. Zwei
 * Fassungen derselben Regel sind genau das, wogegen dieses Projekt sonst Gates
 * baut.
 */
export function deckung(
  cost: Readonly<Record<string, number>>,
  res: Readonly<Record<string, number>>,
): string {
  return Object.entries(cost)
    .map(([id, menge]) => ((res[id] ?? 0) >= menge ? '1' : '0'))
    .join('');
}

/** Nur die Felder, die die Ausbauliste zeigt. */
export interface BuildKeySave {
  built: number;
  finished: boolean;
  era: number;
  res: Readonly<Record<string, number>>;
}

/**
 * Der Schluessel der Ausbauliste.
 *
 * Vorher endete er auf **jeder** gefloorten Ressourcenzahl — der Fehler aus #58,
 * unveraendert, und ausgerechnet an der Liste mit dem `Bauen`-Knopf.
 *
 * Zwei Kostenblöcke zaehlen nur mit, **wenn die Liste sie zeigt**, sonst baute
 * sie neu fuer einen Preis, der gar nicht dasteht:
 *
 * * die Zeile des Heimflugs — sie erscheint erst mit dem Ring;
 * * die Kosten einer **fertigen** Stufe — `${done ? '' : cost}`, und einen Knopf
 *   hat sie auch nicht mehr. Ihre Deckung kann trotzdem kippen, wenn der Vorrat
 *   unter den alten Preis faellt; das ist derselbe Regelverstoss, nur seltener.
 */
export function buildListKey(save: BuildKeySave): string {
  const data = balancing();
  const kette = builds().filter((entry) => entry.minEra === save.era);
  const versatz = builds().filter((entry) => entry.minEra < save.era).length;
  const sprung = save.finished && save.era === data.eraFirst
    ? deckung(data.homeJumpCost, save.res)
    : '-';
  return `${save.built}|${save.finished}|${save.era}|${sprung}#`
    + kette.map((entry, nummer) => {
      const fertig = versatz + nummer < save.built;
      return `${entry.id}:${fertig ? '-' : deckung(entry.cost, save.res)}`;
    }).join('|');
}

/**
 * Der Schluessel der Boosterliste.
 *
 * Zwei Bits je Booster: laeuft er, und ist er bezahlbar. Der Kristallstand war
 * derselbe unnoetig feine Fall wie in der Ausbauliste; die **Restzeit** ist es
 * ebenso — sie ist eine laufende Zahl und wird an Ort und Stelle geschrieben.
 * Gemerkt wird nur, **ob** etwas laeuft, denn davon haengt ab, ob der Zusatz
 * „— aktiv …" ueberhaupt im Markup steht.
 */
export function boosterListKey(
  res: Readonly<Record<string, number>>,
  restzeit: (id: string) => number,
  stapeln = false,
  stacks: Readonly<Record<string, number>> = {},
): string {
  const vorrat = res[balancing().boosterCurrency] ?? 0;
  // Die **Höhe** gehört dazu, nicht nur der Schalter: sie steht als „×2" in der
  // Zeile, und der Knopf wird am Anschlag stumpf. Beides sind Schwellen und
  // keine laufenden Zahlen — sie kippen selten, und genau darum dürfen sie in
  // den Schlüssel (`oberflaeche.md`).
  return `${stapeln ? 's' : 'v'}|` + boosters()
    .map((entry) =>
      `${entry.id}:${restzeit(entry.id) > 0 ? '1' : '0'}${vorrat >= entry.cost ? '1' : '0'}`
      + `${Math.max(1, stacks[entry.id] ?? 1)}`)
    .join('|');
}

/** Nur die Felder, die die Auftragsliste zeigt. */
export interface QuestKeySave {
  era: number;
  built: number;
  quests: Readonly<Record<string, boolean>>;
  sideSteps: Readonly<Record<string, number>>;
  sideClaimed: Readonly<Record<string, boolean>>;
}

/** Der Flottenauftrag, so weit sein Zustand die Liste **strukturell** betrifft. */
export interface FleetKeyState {
  order: string;
  progress: number;
  buffLeft: number;
}

/**
 * Der Schluessel der Auftragsliste.
 *
 * Zwei zu feine Groessen standen darin. Der gefloorte Auftragsfortschritt: er
 * kippt bei einem Ziel wie „25 Brocken" selten, bei einem Ressourcenziel aber
 * mit jeder eingebuchten Einheit. Und `Math.ceil(buffLeft)` — der Feld-Buff
 * laeuft **lokal** herunter (`store.tickFleet`), also baute die Liste jede
 * Sekunde neu, solange ein Buff lief, und nahm den `Belohnung abholen`-Knopf mit.
 *
 * Strukturell ist an einem Auftrag nur, **ob** er abholbar ist und ob er
 * abgeholt wurde. Die Zahl und der Balken sind laufende Werte.
 *
 * Die Schritte der Nebenauftraege bleiben als Zahl im Schluessel: sie steigt nur
 * durch ein Ereignis im Feld, und jede Stufe aendert die gezeichneten Marken.
 */
export function questListKey(
  save: QuestKeySave,
  fortschritt: (id: string) => number,
  fleet: FleetKeyState | null,
): string {
  const offen = quests().filter((entry) => entry.minEra <= save.era);
  const auftraege = offen
    .map((entry) => {
      const reif = fortschritt(entry.id) >= entry.goal.n ? '1' : '0';
      return `${entry.id}:${reif}${save.quests[entry.id] ? 'c' : ''}`;
    })
    .join('|');

  const seite = sideQuests()
    .map((entry) =>
      `${entry.id}:${save.sideSteps[entry.id] ?? 0}${save.sideClaimed[entry.id] ? 'c' : ''}`)
    .join('|');

  const ziel = fleet ? fleetOrder(fleet.order)?.goal.n : undefined;
  const flotte = fleet
    ? `${fleet.order}:${ziel !== undefined && fleet.progress >= ziel ? '1' : '0'}`
      + `${fleet.buffLeft > 0 ? 'b' : ''}`
    : '';

  return `${save.era}§${save.built}§${flotte}§${seite}§${auftraege}`;
}
